import React from "react";
import { EditOutlined, DeleteOutlined, ArrowRightOutlined } from "@ant-design/icons";

/**
 * NOTE ON FIELD NAMES
 * ---------------------------------------------------------------
 * I don't have your real API response shape, so this reads the same
 * fields visible in your screenshot, with safe fallbacks:
 *   plan.name / plan.title
 *   plan.isActive (bool)  -> falls back to plan.status === "active"
 *   plan.description
 *   plan.price
 *   plan.durationDays / plan.duration
 *   plan.planType ("free" | "paid")
 *   plan.maxRestaurants
 *   plan.maxTables
 *   plan.features (array of strings)
 *   plan.createdAt
 * If your real plan object uses different keys, just adjust the
 * getters below — everything else (layout, styling) stays the same.
 * ---------------------------------------------------------------
 */

const FEATURE_PREVIEW_COUNT = 3;

const PlanCardStyles = () => (
  <style>{`
    @keyframes planCardPop {
      from { opacity: 0; transform: translateY(6px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes planLiveDot {
      0% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.55); }
      70% { box-shadow: 0 0 0 6px rgba(16, 185, 129, 0); }
      100% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0); }
    }
    .plan-card-v2 {
      animation: planCardPop 0.35s ease-out both;
      transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
    }
    .plan-card-v2:hover {
      transform: translateY(-3px);
      box-shadow: 0 14px 28px -12px rgba(30, 27, 75, 0.22);
      border-color: rgba(99, 102, 241, 0.35) !important;
    }
    .plan-card-v2:hover .plan-view-hint {
      opacity: 1;
      transform: translateX(0);
    }
    .plan-live-dot {
      animation: planLiveDot 2s infinite;
    }
    .plan-view-hint {
      opacity: 0;
      transform: translateX(-4px);
      transition: opacity 0.2s ease, transform 0.2s ease;
    }
  `}</style>
);

const PlanCard = ({ plan, onView, onEdit, onDelete }) => {
  const name = plan?.name || plan?.title || "Untitled Plan";
  const isActive =
    typeof plan?.isActive === "boolean" ? plan.isActive : plan?.status === "active";
  const planType = (plan?.planType || (Number(plan?.price) === 0 ? "free" : "paid")).toLowerCase();
  const isFree = planType === "free";
  const price = Number(plan?.price ?? 0);
  const durationDays = plan?.durationDays ?? plan?.duration ?? "-";
  const maxRestaurants = plan?.maxRestaurants ?? "-";
  const maxTables = plan?.maxTables ?? "-";
  const features = Array.isArray(plan?.features) ? plan.features : [];
  const previewFeatures = features.slice(0, FEATURE_PREVIEW_COUNT);
  const hiddenCount = Math.max(0, features.length - FEATURE_PREVIEW_COUNT);
  const createdLabel = plan?.createdAt
    ? new Date(plan.createdAt).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "-";

  const accent = isFree
    ? { bar: "linear-gradient(90deg,#94a3b8,#cbd5e1)", chip: "bg-slate-100 text-slate-600 border-slate-200" }
    : { bar: "linear-gradient(90deg,#6366f1,#8b5cf6)", chip: "bg-indigo-50 text-indigo-600 border-indigo-100" };

  const stop = (e) => e.stopPropagation();

  return (
    <>
      <PlanCardStyles />
      <div
        className="plan-card-v2 relative h-full flex flex-col bg-white rounded-2xl border border-gray-200 shadow-sm cursor-pointer overflow-hidden"
        onClick={() => onView?.(plan)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => (e.key === "Enter" ? onView?.(plan) : null)}
      >
        {/* top accent */}
        <div style={{ height: 4, background: accent.bar }} />

        <div className="flex flex-col flex-1 p-4">
          {/* header row */}
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <h3 className="font-semibold text-gray-900 text-[15px] truncate" title={name}>
                {name}
              </h3>
            </div>
            <div className="flex items-center gap-1 shrink-0" onClick={stop}>
              <button
                onClick={() => onEdit?.(plan)}
                className="w-7 h-7 flex items-center justify-center rounded-md text-indigo-600 bg-indigo-50 hover:bg-indigo-100 transition"
                title="Edit plan"
              >
                <EditOutlined style={{ fontSize: 12 }} />
              </button>
              <button
                onClick={() => onDelete?.(plan.id)}
                className="w-7 h-7 flex items-center justify-center rounded-md text-red-500 bg-red-50 hover:bg-red-100 transition"
                title="Delete plan"
              >
                <DeleteOutlined style={{ fontSize: 12 }} />
              </button>
            </div>
          </div>

          {/* status pill */}
          <div className="mt-1.5">
            <span
              className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-0.5 rounded-full border ${
                isActive
                  ? "bg-emerald-50 text-emerald-600 border-emerald-100"
                  : "bg-gray-100 text-gray-500 border-gray-200"
              }`}
            >
              <span
                className={`w-1.5 h-1.5 rounded-full ${
                  isActive ? "bg-emerald-500 plan-live-dot" : "bg-gray-400"
                }`}
              />
              {isActive ? "Active" : "Inactive"}
            </span>
          </div>

          {/* price */}
          <div className="mt-3 flex items-baseline gap-1.5">
            <span className="text-2xl font-bold text-gray-900 tabular-nums">
              {price === 0 ? "Free" : `₹${price.toLocaleString("en-IN")}`}
            </span>
            {price !== 0 && (
              <span className="text-xs text-gray-400">/ {durationDays} days</span>
            )}
            {price === 0 && durationDays !== "-" && (
              <span className="text-xs text-gray-400">for {durationDays} days</span>
            )}
          </div>

          {/* limits row */}
          <div className="mt-3 flex flex-wrap gap-1.5">
            <span className={`text-[11px] px-2 py-0.5 rounded-md border ${accent.chip}`}>
              {maxRestaurants} Restaurant{maxRestaurants === 1 ? "" : "s"}
            </span>
            <span className={`text-[11px] px-2 py-0.5 rounded-md border ${accent.chip}`}>
              {maxTables} Tables
            </span>
          </div>

          {/* features preview */}
          <div className="mt-3 min-h-[26px]">
            {previewFeatures.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {previewFeatures.map((f, i) => (
                  <span
                    key={i}
                    className="text-[11px] px-2 py-0.5 rounded-md bg-gray-50 text-gray-600 border border-gray-200"
                  >
                    {f}
                  </span>
                ))}
                {hiddenCount > 0 && (
                  <span className="text-[11px] px-2 py-0.5 rounded-md bg-gray-900 text-white font-medium">
                    +{hiddenCount} more
                  </span>
                )}
              </div>
            ) : (
              <span className="text-[11px] text-gray-400">No features listed</span>
            )}
          </div>

          {/* footer */}
          <div className="mt-auto pt-3 flex items-center justify-between border-t border-gray-100 mt-3">
            <span className="text-[11px] text-gray-400">Created {createdLabel}</span>
            <span className="plan-view-hint flex items-center gap-1 text-[11px] font-medium text-indigo-600">
              View details <ArrowRightOutlined style={{ fontSize: 10 }} />
            </span>
          </div>
        </div>
      </div>
    </>
  );
};

export default PlanCard;